import {isAfter} from 'date-fns/isAfter';
import {isBefore} from 'date-fns/isBefore';
import {isSameDay} from 'date-fns/isSameDay';
import {isSameMonth} from 'date-fns/isSameMonth';

import {ECalendarLeadDate, THoveredStatus} from '../types';

type THoveredStatusPayload = {
  currentDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  hoveredDate?: Date | null;
  leadDate?: ECalendarLeadDate;
};

const isHoveredDay = (currentDate: Date, startDate: Date | null, endDate: Date | null): boolean => {
  if (!endDate || !startDate) {
    return isSameDay(currentDate, startDate ?? 0) || isSameDay(currentDate, endDate ?? 0);
  }

  return (
    (isAfter(currentDate, startDate) && isBefore(currentDate, endDate)) ||
    isSameDay(currentDate, startDate) ||
    isSameDay(currentDate, endDate)
  );
};

const getDayHoveredStatus = ({
  currentDate,
  startDate,
  endDate,
  hoveredDate,
  leadDate = ECalendarLeadDate.START,
}: THoveredStatusPayload): THoveredStatus | null => {
  if (!currentDate || ((!startDate || !endDate) && !hoveredDate)) {
    return null;
  }

  if (!hoveredDate || (startDate && endDate)) {
    return {
      isHovered:
        isHoveredDay(currentDate, startDate, endDate) &&
        !!startDate &&
        !!endDate &&
        !isSameDay(startDate, endDate),
      isStartHovered: !!startDate && isSameDay(currentDate, startDate),
      isEndHovered: !!endDate && isSameDay(currentDate, endDate),
    };
  }

  const compareDate = leadDate === ECalendarLeadDate.START ? endDate : startDate;

  if (!compareDate) {
    return null;
  }

  if (isAfter(hoveredDate, compareDate)) {
    return {
      isHovered:
        isHoveredDay(currentDate, compareDate, hoveredDate) && !isSameDay(hoveredDate, compareDate),
      isStartHovered: isSameDay(currentDate, compareDate),
      isEndHovered: isSameDay(currentDate, hoveredDate),
    };
  }

  return {
    isHovered:
      isHoveredDay(currentDate, hoveredDate, compareDate) && !isSameDay(hoveredDate, compareDate),
    isStartHovered: isSameDay(currentDate, hoveredDate),
    isEndHovered: isSameDay(currentDate, compareDate),
  };
};

const isHoveredMonth = (
  currentDate: Date,
  startDate: Date | null,
  endDate: Date | null,
): boolean => {
  if (!endDate || !startDate) {
    return false;
  }

  return (
    (isAfter(currentDate, startDate) && isBefore(currentDate, endDate)) ||
    isSameMonth(currentDate, startDate) ||
    isSameMonth(currentDate, endDate)
  );
};

const getMonthHoveredStatus = ({
  currentDate,
  startDate,
  endDate,
  hoveredDate,
  leadDate = ECalendarLeadDate.START,
}: THoveredStatusPayload): THoveredStatus | null => {
  if (!currentDate || (!startDate && !endDate)) {
    return null;
  }

  if (!hoveredDate || (startDate && endDate)) {
    return {
      isHovered:
        !!startDate &&
        !!endDate &&
        isHoveredMonth(currentDate, startDate, endDate) &&
        !isSameMonth(startDate, endDate),
      isStartHovered: !!startDate && isSameMonth(currentDate, startDate),
      isEndHovered: !!endDate && isSameMonth(currentDate, endDate),
    };
  }

  if (
    !hoveredDate ||
    (startDate && isSameMonth(hoveredDate, startDate)) ||
    (endDate && isSameMonth(hoveredDate, endDate))
  ) {
    return null;
  }

  const compareDate = leadDate === ECalendarLeadDate.START ? endDate : startDate;

  if (!compareDate) {
    return null;
  }

  if (isAfter(hoveredDate, compareDate)) {
    return {
      isHovered: isHoveredMonth(currentDate, compareDate, hoveredDate),
      isStartHovered: isSameMonth(currentDate, compareDate),
      isEndHovered: isSameMonth(currentDate, hoveredDate),
    };
  }

  return {
    isHovered: isHoveredMonth(currentDate, hoveredDate, compareDate),
    isStartHovered: isSameMonth(currentDate, hoveredDate),
    isEndHovered: !!compareDate && isSameMonth(currentDate, compareDate),
  };
};

const getYearHoveredStatus = ({
  currentDate,
  startDate,
  endDate,
  hoveredDate,
  leadDate = ECalendarLeadDate.START,
}: THoveredStatusPayload): THoveredStatus | null => {
  if (!currentDate || (!startDate && !endDate)) {
    return null;
  }

  const startYear = startDate?.getFullYear();
  const endYear = endDate?.getFullYear();
  const currentYear = currentDate.getFullYear();
  const hoveredYear = hoveredDate?.getFullYear();

  if (!hoveredDate || (startYear && endYear)) {
    return {
      isHovered:
        !!startYear &&
        !!endYear &&
        currentYear >= startYear &&
        currentYear <= endYear &&
        startYear !== endYear,
      isStartHovered: currentYear === startYear,
      isEndHovered: currentYear === endYear,
    };
  }

  const compareYear = leadDate === ECalendarLeadDate.START ? endYear : startYear;

  if (!hoveredYear || !compareYear || hoveredYear === startYear || hoveredYear === endYear) {
    return null;
  }

  if (hoveredYear > compareYear) {
    return {
      isHovered: currentYear >= compareYear && currentYear <= hoveredYear,
      isStartHovered: currentYear === compareYear,
      isEndHovered: currentYear === hoveredYear,
    };
  }

  return {
    isHovered: currentYear >= hoveredYear && currentYear <= compareYear,
    isStartHovered: currentYear === hoveredYear,
    isEndHovered: currentYear === compareYear,
  };
};

export {getDayHoveredStatus, getMonthHoveredStatus, getYearHoveredStatus};
